/**
 * MUSKLE PWA install card — storefront helper.
 *
 * Standalone / display-mode checks are client UX signals only.
 * They are not secure installation attestation. The server awards
 * the one-time bonus using App Proxy customer identity + ledger rules.
 *
 * "already_claimed" means the one-time bonus was paid — not that the
 * app is permanently installed. Customers may always reinstall; the
 * button never awards Points. Backend remains authoritative.
 */
(function () {
  'use strict';

  var PROXY_CLAIM = '/apps/muskle-points/bonus/pwa-install';
  var PROXY_STATUS = '/apps/muskle-points/bonus/pwa-install';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function isStandaloneLaunch() {
    try {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }
    } catch (e) {}
    if (typeof navigator !== 'undefined' && navigator.standalone === true) {
      return true;
    }
    return false;
  }

  function isIosLike() {
    var ua = navigator.userAgent || '';
    var iOS = /iPad|iPhone|iPod/.test(ua);
    var iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOS || iPadOs;
  }

  function isAndroid() {
    return /Android/i.test(navigator.userAgent || '');
  }

  function isSamsungInternet() {
    return /SamsungBrowser/i.test(navigator.userAgent || '');
  }

  function hasNativeInstallPrompt(root) {
    var deferred = root && root._muskleDeferredPrompt;
    return !!(deferred && typeof deferred.prompt === 'function');
  }

  function isBonusClaimed(root) {
    return root.getAttribute('data-muskle-pwa-claimed') === '1';
  }

  function readCopy(root) {
    var node = qs(root, '[data-muskle-pwa-copy]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function setBalanceText(root, balance) {
    var nodes = root.querySelectorAll('[data-muskle-pwa-balance]');
    for (var i = 0; i < nodes.length; i += 1) {
      nodes[i].textContent = String(balance);
    }
  }

  function show(el, on) {
    if (!el) return;
    el.hidden = !on;
  }

  function setHelp(root, text) {
    var help = qs(root, '[data-muskle-pwa-help]');
    if (!help) return;
    if (!text) {
      help.hidden = true;
      help.textContent = '';
      return;
    }
    help.hidden = false;
    help.textContent = text;
  }

  function setStatusText(root, text) {
    var el = qs(root, '[data-muskle-pwa-status-text]');
    if (!el) return;
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = text;
  }

  function setInstallButtonLabel(root, label) {
    var installBtn = qs(root, '[data-muskle-pwa-install-btn]');
    if (!installBtn || !label) return;
    installBtn.textContent = label;
  }

  function updateReinstallButtonLabel(root) {
    var copy = root._musklePwaCopy || {};
    if (hasNativeInstallPrompt(root) && !isIosLike()) {
      setInstallButtonLabel(root, copy.btnReinstall || copy.btnLabel || '');
      root.setAttribute('data-muskle-pwa-reinstall-mode', 'native');
      return;
    }
    setInstallButtonLabel(root, copy.btnReadd || copy.btnLabel || '');
    root.setAttribute('data-muskle-pwa-reinstall-mode', 'manual');
  }

  function setActionMode(root, mode) {
    // mode: install | login | processing | hidden
    var installBtn = qs(root, '[data-muskle-pwa-install-btn]');
    var loginBtn = qs(root, '[data-muskle-pwa-login-btn]');
    var processing = qs(root, '[data-muskle-pwa-processing]');
    show(installBtn, mode === 'install');
    show(loginBtn, mode === 'login');
    show(processing, mode === 'processing');
  }

  function hideSuccess(root) {
    show(qs(root, '[data-muskle-pwa-success-title]'), false);
    show(qs(root, '[data-muskle-pwa-success-body]'), false);
    show(qs(root, '[data-muskle-pwa-retry-wrap]'), false);
  }

  /**
   * Bonus already claimed ≠ app permanently installed.
   * CASE 1 standalone → installed status, no button.
   * CASE 2 native prompt → "opnieuw installeren".
   * CASE 3/4 browser without prompt → "opnieuw toevoegen" + instructions.
   */
  function applyAlreadyClaimedUi(root) {
    var copy = root._musklePwaCopy || {};
    root.setAttribute('data-muskle-pwa-claimed', '1');
    root.setAttribute('data-muskle-pwa-state', 'already_claimed');
    hideSuccess(root);
    show(qs(root, '[data-muskle-pwa-retry-wrap]'), false);
    setStatusText(root, copy.already || '');

    if (isStandaloneLaunch()) {
      setActionMode(root, 'hidden');
      setHelp(root, copy.installed || '');
      root.setAttribute('data-muskle-pwa-reinstall-mode', 'standalone');
      return;
    }

    updateReinstallButtonLabel(root);
    setActionMode(root, 'install');
    setHelp(root, reinstallHelp(root));
  }

  function setState(root, state) {
    root.setAttribute('data-muskle-pwa-state', state);
    var copy = root._musklePwaCopy || {};
    hideSuccess(root);
    show(qs(root, '[data-muskle-pwa-retry-wrap]'), false);

    if (state === 'loading') {
      setActionMode(root, 'hidden');
      setHelp(root, '');
      setStatusText(root, copy.loading || '');
      return;
    }

    if (state === 'logged_out') {
      setActionMode(root, 'login');
      setHelp(root, '');
      setStatusText(root, copy.login || '');
      return;
    }

    if (state === 'feature_disabled') {
      setActionMode(root, 'hidden');
      setHelp(root, '');
      setStatusText(root, copy.disabled || '');
      return;
    }

    if (state === 'already_claimed') {
      applyAlreadyClaimedUi(root);
      return;
    }

    if (state === 'credited') {
      root.setAttribute('data-muskle-pwa-claimed', '1');
      setActionMode(root, 'hidden');
      setHelp(root, isStandaloneLaunch() ? copy.installed || '' : '');
      setStatusText(root, '');
      show(qs(root, '[data-muskle-pwa-success-title]'), true);
      show(qs(root, '[data-muskle-pwa-success-body]'), true);
      return;
    }

    if (state === 'processing') {
      setActionMode(root, 'processing');
      setHelp(root, '');
      setStatusText(root, '');
      return;
    }

    if (state === 'temporary_error') {
      if (isBonusClaimed(root) && !isStandaloneLaunch()) {
        updateReinstallButtonLabel(root);
        setActionMode(root, 'install');
        setHelp(root, reinstallHelp(root));
      } else {
        setInstallButtonLabel(root, copy.btnLabel || '');
        setActionMode(root, 'install');
        setHelp(root, defaultHelp(root));
      }
      setStatusText(root, copy.error || '');
      show(qs(root, '[data-muskle-pwa-retry-wrap]'), true);
      return;
    }

    // install_ready — keep primary button visible (first-time or eligible)
    setInstallButtonLabel(root, copy.btnLabel || '');
    setActionMode(root, 'install');
    setHelp(root, defaultHelp(root));
    setStatusText(root, '');
  }

  function defaultHelp(root) {
    var copy = root._musklePwaCopy || {};
    if (isIosLike()) return copy.helpIos || '';
    if (isAndroid()) return copy.helpAndroid || '';
    return copy.helpDesktop || '';
  }

  function reinstallHelp(root) {
    var copy = root._musklePwaCopy || {};
    if (isIosLike()) return copy.helpReinstallIos || copy.helpIos || '';
    if (isAndroid()) return copy.helpReinstallAndroid || copy.helpAndroid || '';
    return copy.helpReinstallDesktop || copy.helpDesktop || '';
  }

  function parseJsonSafe(res) {
    return res.text().then(function (text) {
      try {
        return { ok: res.ok, status: res.status, body: JSON.parse(text || '{}') };
      } catch (e) {
        return { ok: false, status: res.status, body: { ok: false, status: 'temporary_error' } };
      }
    });
  }

  function fetchStatus() {
    return fetch(PROXY_STATUS, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(parseJsonSafe);
  }

  function claimBonus() {
    return fetch(PROXY_CLAIM, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    }).then(parseJsonSafe);
  }

  function closeModal(root) {
    var modal = qs(root, '[data-muskle-pwa-modal]');
    if (modal) modal.hidden = true;
  }

  function openModal(root, opts) {
    opts = opts || {};
    var modal = qs(root, '[data-muskle-pwa-modal]');
    if (!modal) return;
    var titleEl = qs(root, '[data-muskle-pwa-modal-title]');
    var listEl = qs(root, '[data-muskle-pwa-modal-list]');
    var bodyEl = qs(root, '[data-muskle-pwa-modal-body]');
    if (titleEl) titleEl.textContent = opts.title || '';
    if (listEl) {
      listEl.innerHTML = '';
      var steps = opts.steps || [];
      if (steps.length) {
        listEl.hidden = false;
        for (var i = 0; i < steps.length; i += 1) {
          var li = document.createElement('li');
          li.textContent = steps[i];
          listEl.appendChild(li);
        }
      } else {
        listEl.hidden = true;
      }
    }
    if (bodyEl) {
      if (opts.body) {
        bodyEl.hidden = false;
        bodyEl.textContent = opts.body;
      } else {
        bodyEl.hidden = true;
        bodyEl.textContent = '';
      }
    }
    modal.hidden = false;
  }

  function openDeviceInstructions(root) {
    var copy = root._musklePwaCopy || {};
    var modal = copy.modal || {};
    var claimed = isBonusClaimed(root);

    if (isIosLike()) {
      openModal(root, {
        title: claimed ? modal.iosReinstallTitle || modal.iosTitle : modal.iosTitle,
        steps: claimed
          ? modal.iosReinstallSteps || modal.iosSteps || []
          : modal.iosSteps || [],
      });
      return;
    }
    if (isAndroid()) {
      var steps = (
        claimed
          ? modal.androidReinstallSteps || modal.androidSteps || []
          : modal.androidSteps || []
      ).slice();
      if (isSamsungInternet() && modal.samsungStep2 && steps.length > 1) {
        steps[1] = modal.samsungStep2;
      }
      openModal(root, {
        title: claimed
          ? modal.androidReinstallTitle || modal.androidTitle
          : modal.androidTitle,
        steps: steps,
      });
      return;
    }
    openModal(root, {
      title: claimed
        ? modal.desktopReinstallTitle || modal.desktopTitle
        : modal.desktopTitle,
      body: claimed
        ? modal.desktopReinstallBody || modal.desktopBody
        : modal.desktopBody,
      steps: [],
    });
  }

  function applyServerOutcome(root, result) {
    var body = result.body || {};
    var status = body.status;

    if (result.status === 401 || status === 'login_required') {
      setState(root, 'logged_out');
      return;
    }
    if (status === 'feature_disabled' || result.status === 403) {
      // Still allow install UX; bonus may be gated server-side.
      // Show install button so customers can add the icon; claim later when enabled.
      if (isStandaloneLaunch()) {
        setState(root, 'feature_disabled');
        return;
      }
      setState(root, 'install_ready');
      return;
    }
    if (status === 'rate_limited' || result.status === 429) {
      setState(root, 'temporary_error');
      return;
    }
    if (status === 'credited') {
      if (typeof body.balance === 'number') setBalanceText(root, body.balance);
      setState(root, 'credited');
      root.setAttribute('data-muskle-pwa-terminal', 'credited');
      return;
    }
    if (status === 'already_claimed') {
      if (typeof body.balance === 'number') setBalanceText(root, body.balance);
      setState(root, 'already_claimed');
      root.setAttribute('data-muskle-pwa-terminal', 'already_claimed');
      return;
    }
    if (status === 'eligible' || status === 'ok' || result.ok) {
      if (typeof body.balance === 'number') setBalanceText(root, body.balance);
      if (isStandaloneLaunch()) {
        maybeAutoClaim(root);
        return;
      }
      setState(root, 'install_ready');
      return;
    }
    setState(root, 'temporary_error');
  }

  function maybeAutoClaim(root) {
    if (!isStandaloneLaunch()) return;
    if (root.getAttribute('data-muskle-pwa-terminal')) return;
    if (root.getAttribute('data-muskle-pwa-claiming') === '1') return;

    root.setAttribute('data-muskle-pwa-claiming', '1');
    setState(root, 'processing');

    // Standalone launch detected; server will apply the one-time customer bonus if eligible.
    claimBonus()
      .then(function (result) {
        applyServerOutcome(root, result);
      })
      .catch(function () {
        setState(root, 'temporary_error');
      })
      .then(function () {
        root.removeAttribute('data-muskle-pwa-claiming');
      });
  }

  function onInstallClick(root, event) {
    event.preventDefault();
    // Never award Points from this button — install / reinstall UX only.
    var deferred = root._muskleDeferredPrompt;
    var copy = root._musklePwaCopy || {};
    var claimed = isBonusClaimed(root);

    if (deferred && typeof deferred.prompt === 'function') {
      deferred
        .prompt()
        .then(function () {
          return deferred.userChoice;
        })
        .then(function (choice) {
          root._muskleDeferredPrompt = null;
          var outcome = choice && choice.outcome ? choice.outcome : 'unknown';
          root.setAttribute('data-muskle-pwa-prompt-outcome', outcome);
          if (claimed) {
            updateReinstallButtonLabel(root);
            setActionMode(root, 'install');
            setHelp(
              root,
              copy.helpAfterReinstallPrompt || copy.helpReinstallAndroid || reinstallHelp(root)
            );
            setStatusText(root, copy.already || '');
            root.setAttribute('data-muskle-pwa-state', 'already_claimed');
            return;
          }
          setHelp(root, copy.helpAfterPrompt || defaultHelp(root));
          setState(root, 'install_ready');
        })
        .catch(function () {
          openDeviceInstructions(root);
        });
      return;
    }

    openDeviceInstructions(root);
  }

  function bindCard(root) {
    root._musklePwaCopy = readCopy(root);
    if (isIosLike()) root.setAttribute('data-muskle-pwa-device', 'ios');
    else if (isSamsungInternet()) root.setAttribute('data-muskle-pwa-device', 'samsung');
    else if (isAndroid()) root.setAttribute('data-muskle-pwa-device', 'android');
    else root.setAttribute('data-muskle-pwa-device', 'desktop');

    var installBtn = qs(root, '[data-muskle-pwa-install-btn]');
    if (installBtn && !installBtn._muskleBound) {
      installBtn._muskleBound = true;
      installBtn.addEventListener('click', function (event) {
        onInstallClick(root, event);
      });
    }

    var closes = root.querySelectorAll('[data-muskle-pwa-modal-close]');
    for (var i = 0; i < closes.length; i += 1) {
      if (closes[i]._muskleBound) continue;
      closes[i]._muskleBound = true;
      closes[i].addEventListener('click', function () {
        closeModal(root);
      });
    }

    var retry = qs(root, '[data-muskle-pwa-retry]');
    if (retry && !retry._muskleBound) {
      retry._muskleBound = true;
      retry.addEventListener('click', function (event) {
        event.preventDefault();
        initCard(root);
      });
    }
  }

  function initCard(root) {
    bindCard(root);
    setState(root, 'loading');

    // Show the install button immediately so the card is never text-only while waiting.
    if (!isStandaloneLaunch()) {
      setActionMode(root, 'install');
      setHelp(root, defaultHelp(root));
    }

    fetchStatus()
      .then(function (result) {
        var body = result.body || {};
        if (body.already_claimed || body.status === 'already_claimed') {
          if (typeof body.balance === 'number') setBalanceText(root, body.balance);
          setState(root, 'already_claimed');
          root.setAttribute('data-muskle-pwa-terminal', 'already_claimed');
          return;
        }
        if (result.status === 401 || body.status === 'login_required') {
          // Keep install path available for logged-out UX via login CTA.
          setState(root, 'logged_out');
          return;
        }
        if (isStandaloneLaunch()) {
          maybeAutoClaim(root);
          return;
        }
        // feature_disabled still shows install button (icon add), claim gated later
        setState(root, 'install_ready');
      })
      .catch(function () {
        if (isStandaloneLaunch()) {
          setState(root, 'temporary_error');
          return;
        }
        setState(root, 'install_ready');
      });
  }

  function onBeforeInstallPrompt(event) {
    event.preventDefault();
    var cards = document.querySelectorAll('[data-muskle-pwa-install-card]');
    for (var i = 0; i < cards.length; i += 1) {
      cards[i]._muskleDeferredPrompt = event;
      var state = cards[i].getAttribute('data-muskle-pwa-state');
      if (state === 'already_claimed' && !isStandaloneLaunch()) {
        updateReinstallButtonLabel(cards[i]);
        setActionMode(cards[i], 'install');
        setHelp(cards[i], reinstallHelp(cards[i]));
        continue;
      }
      if (state === 'install_ready' || state === 'loading') {
        setHelp(cards[i], (cards[i]._musklePwaCopy || {}).helpAndroid || defaultHelp(cards[i]));
      }
    }
  }

  function boot() {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    var cards = document.querySelectorAll('[data-muskle-pwa-install-card]');
    for (var i = 0; i < cards.length; i += 1) {
      initCard(cards[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
